/** @file labelsOf: this frame's viewport labels (lock reason at the gizmo centre, drag readout, yaw zero). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { cssPoint } from './cssPoint';
import { gizmoAccess } from './gizmoAccess';
import { gizmoFor } from './gizmoFor';
import type { GizmoFrame } from './gizmoFrame';
import type { LabelSpot, LabelsInput } from './viewportLabels';
import type { ViewportCtx } from './viewportCtx';

/** The `0° S` label sits this far out (gizmo radii) at the zero tick. */
const TICK_LABEL_AT = 1.32;

/** The yaw ring's zero label beside the tick (`f.yawZeroRad` on the Z ring), while the rotate gizmo shows that ring. */
function tickSpot(ctx: ViewportCtx, f: GizmoFrame | null): LabelSpot | null {
  if (f?.tool !== 'rotate' || (f.show & 4) === 0) return null;
  const [c, s, k] = [Math.cos(f.yawZeroRad), Math.sin(f.yawZeroRad), f.size * TICK_LABEL_AT];
  const [o, x, y] = [f.model.origin, f.model.axes[0], f.model.axes[1]];
  const p: Vec3 = [o[0] + k * (c * x[0] + s * y[0]), o[1] + k * (c * x[1] + s * y[1]), o[2] + k * (c * x[2] + s * y[2])];
  return { text: '0° S', at: cssPoint(ctx, p) };
}

/**
 * Edit mode only: `lock` = `gizmoAccess`'s reason where the gizmo centre would be (the handles are not drawn);
 * `readout` = the running drag's value beside the pointer; `tick` = the yaw ring's `0° S` (`gizmo` is this frame's).
 */
export function labelsOf(ctx: ViewportCtx, gizmo: GizmoFrame | null): LabelsInput | null {
  if (ctx.mode !== 'edit') return null;
  const at = gizmoFor(ctx);
  const lock = at === null ? null : gizmoAccess(ctx.store.getState(), at.tool).lock;
  const edit = ctx.edit;
  return {
    lock: at === null || lock === null ? null : { text: lock, at: cssPoint(ctx, at.model.origin) },
    readout: edit?.readout === null || edit === null ? null : { text: edit.readout, at: edit.cursor },
    tick: tickSpot(ctx, gizmo),
  };
}
